import { ref } from '@nuxtjs/composition-api';
import Analytics from '@/services/Analytics';
import { error as DDError } from '@/services/Datadog';
import { pay } from '@/services/Payment';
import { trialPrice } from '@/hooks/useTrials';

/** What plan/convert is sent for the $1 trial (billing-service CompanyPlanConvertInput). */
export interface RecurlyConvertPayload {
  recurlyTokenId: string;
  plan: string;
  setupFee: number;
  trial: number;
  originUrl: string;
  marketing: Record<string, any>;
}

export const REQUIRES_ACTION_MESSAGE =
  'Your card issuer asked for extra verification. Please use another card.';

/** The lead-facing message for a failed convert (billing-service BIO-1864 response contract). */
export const conversionErrorMessage = (err: any): string => {
  const { status, data } = err?.response || {};
  if (status === 402) {
    return `Payment declined: ${data?.message || 'please try another card.'}`;
  }
  if ((status === 400 || status === 409) && data?.message) {
    return data.message;
  }

  return 'We could not process your payment. Please try again.';
};

/**
 * Converts the lead through Recurly (BIO-1872), ported from app's useRecurlyConvert. `convert`
 * resolves the converted company, or null when the lead has to try another card; `error` then
 * says why. One convert at a time: a second submit while one is in flight is ignored, and the
 * token id never reaches analytics.
 */
const useRecurlyConvert = () => {
  const isConverting = ref(false);
  const error = ref('');

  const convert = async (
    companyId: string,
    payload: RecurlyConvertPayload,
    method: string
  ) => {
    if (isConverting.value) {
      return null;
    }
    isConverting.value = true;
    error.value = '';

    const eventPayload = {
      ...payload,
      recurlyTokenId: null,
      method,
      amountPaidToday: trialPrice,
    };
    Analytics.track('submitted-new-subscription-payment', eventPayload);

    try {
      const company = await pay(companyId, payload);
      if (company?.requiresAction) {
        // No 3-D Secure challenge, as in honey and app: ask for another card.
        error.value = REQUIRES_ACTION_MESSAGE;

        return null;
      }

      return company;
    } catch (err: any) {
      error.value = conversionErrorMessage(err);
      DDError('Error with payment', err);
      Analytics.track('failed-new-subscription-payment', {
        ...eventPayload,
        status: err?.response?.status,
      });

      return null;
    } finally {
      isConverting.value = false;
    }
  };

  return {
    convert,
    error,
    isConverting,
  };
};

export default useRecurlyConvert;
